/**
 * Timeline Grid Component
 * Custom Atlassian-style timeline grid for on-call assignments
 */

import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { User } from 'lucide-react'

import { TimelineBarItem, TimelineDateRange } from '../../../../types/timeline'
import { generateDateAxis, isToday } from '../utils/dateUtils'

interface TimelineGridProps {
    data: TimelineBarItem[]
    dateRange: TimelineDateRange
    onBarClick?: (item: TimelineBarItem, event: React.MouseEvent) => void
}

// Layer configuration - Only Base (scheduled) and Override (swaps)
const LAYERS: { key: 'base' | 'override'; label: string }[] = [
    { key: 'base', label: 'Scheduled' },
    { key: 'override', label: 'Swap' },
]

// Day abbreviations
const DAY_ABBR = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

// Bar height and spacing
const BAR_HEIGHT = 32
const BAR_GAP = 4
const PADDING_Y = 8

interface BarWithRow extends TimelineBarItem {
    row: number
    startDay: number
    endDay: number
    startFraction: number // Hour fraction within start day (0-1)
    endFraction: number // Hour fraction within end day (0-1)
}

/**
 * Assign rows to overlapping bars to prevent visual overlap
 */
const assignRowsToBar = (
    items: TimelineBarItem[],
    dateRange: TimelineDateRange
): BarWithRow[] => {
    const rangeStart = dayjs(dateRange.start).startOf('day')

    // Calculate start/end days for each item
    const itemsWithDays = items.map((item) => {
        const startTime = dayjs(item.value[1])
        const endTime = dayjs(item.value[2])

        // Calculate start day index
        const startDay = Math.max(
            0,
            startTime.startOf('day').diff(rangeStart, 'day')
        )

        // Calculate hour fractions within the day (0-1)
        const startHour = startTime.hour() + startTime.minute() / 60
        const endHour = endTime.hour() + endTime.minute() / 60
        const startFraction = startHour / 24
        // If end time is midnight (0:00), treat it as end of day (1.0)
        const endFraction = endHour === 0 ? 1 : endHour / 24

        // Check if shift crosses midnight to a different calendar day
        const crossesMidnight = !endTime.isSame(startTime, 'day')

        // Calculate duration in hours
        const durationHours = endTime.diff(startTime, 'hour')

        // Determine end day based on whether shift crosses midnight
        let endDay: number
        if (!crossesMidnight) {
            // Same calendar day - single day shift
            endDay = startDay
        } else if (durationHours <= 24) {
            // Crosses midnight but <= 24 hours - calculate actual end day
            const adjustedEndTime = endTime.subtract(1, 'minute')
            endDay = Math.max(
                startDay,
                adjustedEndTime.startOf('day').diff(rangeStart, 'day')
            )
        } else {
            // Multi-day shift > 24 hours
            const adjustedEndTime = endTime.subtract(1, 'minute')
            endDay = Math.max(
                startDay,
                adjustedEndTime.startOf('day').diff(rangeStart, 'day')
            )
        }

        return { ...item, startDay, endDay, startFraction, endFraction, row: 0 }
    })

    // Sort by start time, then by duration (longer first)
    itemsWithDays.sort((a, b) => {
        const startA = a.value[1] as number
        const startB = b.value[1] as number
        if (startA !== startB) return startA - startB
        // Longer duration first
        const durationA = (a.value[2] as number) - startA
        const durationB = (b.value[2] as number) - startB
        return durationB - durationA
    })

    // Assign rows - greedy algorithm using actual end timestamps
    const rowEndTimes: number[] = [] // Track when each row becomes free (timestamp)

    itemsWithDays.forEach((item) => {
        const itemStartTime = item.value[1] as number

        // Find the first row where this item fits (row end time <= item start time)
        let assignedRow = -1
        for (let r = 0; r < rowEndTimes.length; r++) {
            if (rowEndTimes[r] <= itemStartTime) {
                assignedRow = r
                break
            }
        }

        const itemEndTime = item.value[2] as number

        if (assignedRow === -1) {
            // Need a new row
            assignedRow = rowEndTimes.length
            rowEndTimes.push(itemEndTime)
        } else {
            rowEndTimes[assignedRow] = itemEndTime
        }

        item.row = assignedRow
    })

    return itemsWithDays
}

const TimelineGrid: React.FC<TimelineGridProps> = ({
    data,
    dateRange,
    onBarClick,
}) => {
    // Generate dates for the range
    const dates = useMemo(() => generateDateAxis(dateRange), [dateRange])

    // Group data by layer and assign rows (only base and override)
    const dataByLayer = useMemo(() => {
        const grouped: Record<'base' | 'override', TimelineBarItem[]> = {
            base: [],
            override: [],
        }
        data.forEach((item) => {
            // Map 'final' to 'base' for backward compatibility
            const layer = item.layer === 'final' ? 'base' : item.layer
            if (layer === 'base' || layer === 'override') {
                grouped[layer].push(item)
            }
        })

        // Assign rows for each layer
        const result: Record<
            'base' | 'override',
            { bars: BarWithRow[]; rowCount: number }
        > = {
            base: { bars: [], rowCount: 1 },
            override: { bars: [], rowCount: 1 },
        }

        Object.keys(grouped).forEach((layer) => {
            const layerKey = layer as 'base' | 'override'
            const bars = assignRowsToBar(grouped[layerKey], dateRange)
            const maxRow =
                bars.length > 0 ? Math.max(...bars.map((b) => b.row)) + 1 : 1
            result[layerKey] = { bars, rowCount: maxRow }
        })

        return result
    }, [data, dateRange])

    // Calculate bar position with hour-level precision
    const getBarStyle = (item: BarWithRow, totalColumns: number) => {
        // Calculate percentage-based positioning with hour fractions
        // For same-day shifts, use fractional positioning within the column
        // For multi-day shifts, start fraction applies to first day, end fraction to last day

        const isSameDay = item.startDay === item.endDay

        let leftPercent: number
        let widthPercent: number

        if (isSameDay) {
            // Single day shift - position within the column based on hours
            leftPercent =
                ((item.startDay + item.startFraction) / totalColumns) * 100
            widthPercent =
                ((item.endFraction - item.startFraction) / totalColumns) * 100
        } else {
            // Multi-day shift
            // Start from the fractional position on the first day
            leftPercent =
                ((item.startDay + item.startFraction) / totalColumns) * 100
            // Width spans from start fraction to end fraction across multiple days
            const totalDaySpan =
                item.endDay -
                item.startDay +
                item.endFraction -
                item.startFraction
            widthPercent = (totalDaySpan / totalColumns) * 100
        }

        // Minimum width for visibility (roughly 2 hours worth)
        const minWidthPercent = (2 / 24 / totalColumns) * 100

        return {
            left: `${leftPercent}%`,
            width: `${Math.max(widthPercent, minWidthPercent)}%`,
            top: PADDING_Y + item.row * (BAR_HEIGHT + BAR_GAP),
            backgroundColor: item.itemStyle.color,
        }
    }

    // Render a single layer
    const renderLayer = (layer: 'base' | 'override', label: string) => {
        const { bars: layerData, rowCount } = dataByLayer[layer]
        const hasData = layerData.length > 0
        const contentHeight =
            PADDING_Y * 2 + rowCount * BAR_HEIGHT + (rowCount - 1) * BAR_GAP

        return (
            <div
                key={layer}
                className="border-b border-gray-200 last:border-b-0"
            >
                {/* Layer Header */}
                <div className="flex items-center bg-gray-50 border-b border-gray-200">
                    <div className="w-24 px-3 py-2 text-xs font-semibold text-gray-700 border-r border-gray-200">
                        {label}
                    </div>
                    <div
                        className="flex-1 grid"
                        style={{
                            gridTemplateColumns: `repeat(${dates.length}, minmax(40px, 1fr))`,
                        }}
                    >
                        {dates.map((date, idx) => (
                            <div
                                key={idx}
                                className={`
                                    text-center py-2 text-xs border-r border-gray-100 last:border-r-0
                                    ${isToday(date) ? 'bg-teal-50' : ''}
                                `}
                            >
                                <div className="text-gray-400">
                                    {DAY_ABBR[date.day()]}
                                </div>
                                <div
                                    className={`font-medium ${isToday(date) ? 'text-teal-600' : 'text-gray-700'}`}
                                >
                                    {date.format('D')}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Layer Content */}
                <div
                    className="flex"
                    style={{ minHeight: Math.max(60, contentHeight) }}
                >
                    {/* Rotation label */}
                    <div className="w-24 px-3 py-3 text-xs text-gray-500 border-r border-gray-200 flex items-start">
                        <span className="text-gray-400">Rotation1</span>
                    </div>

                    {/* Grid with bars */}
                    <div className="flex-1 relative">
                        {/* Background grid lines */}
                        <div
                            className="absolute inset-0 grid"
                            style={{
                                gridTemplateColumns: `repeat(${dates.length}, minmax(40px, 1fr))`,
                            }}
                        >
                            {dates.map((date, idx) => (
                                <div
                                    key={idx}
                                    className={`
                                        h-full border-r border-gray-100 last:border-r-0
                                        ${isToday(date) ? 'bg-teal-50/50' : ''}
                                    `}
                                />
                            ))}
                        </div>

                        {/* Bars with absolute positioning */}
                        {layerData.map((item: BarWithRow, idx: number) => {
                            const style = getBarStyle(item, dates.length)
                            return (
                                <div
                                    key={`${item.assignmentId}-${idx}`}
                                    className="absolute h-8 rounded flex items-center gap-2 px-2 cursor-pointer hover:opacity-90 hover:shadow-md transition-all overflow-hidden"
                                    style={{
                                        left: style.left,
                                        width: style.width,
                                        top: style.top,
                                        height: BAR_HEIGHT,
                                        backgroundColor: style.backgroundColor,
                                    }}
                                    onClick={(e) => onBarClick?.(item, e)}
                                >
                                    <div className="w-5 h-5 rounded-full bg-white/30 flex items-center justify-center flex-shrink-0">
                                        <User className="w-3 h-3 text-white" />
                                    </div>
                                    <span className="text-white text-xs font-medium truncate">
                                        {item.employeeName}
                                    </span>
                                </div>
                            )
                        })}

                        {/* Empty state for layer */}
                        {!hasData && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-xs text-gray-300">-</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            {LAYERS.map(({ key, label }) => renderLayer(key, label))}
        </div>
    )
}

export default TimelineGrid
